'use client';

import { llmLogStreamRoute } from '@/app/api/llm-log/stream/route.def';
import { Button } from '@/components/common/Button';
import { LocalDate } from '@/components/common/LocalDate';
import { streamApi } from '@/domains/common/api-client';
import {
  clearRunProgress,
  getRunProgressBuffer,
  subscribeRunProgress,
  subscribeRunProgressClear,
  type RunProgressEvent,
} from '@/domains/pipeline/run-progress';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type LlmEvent = {
  ts: number;
  task: string;
  task_label: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: number;
};

type Row =
  | { kind: 'pipeline'; ts: number; agent: string; body: string; meta: null }
  | { kind: 'llm'; ts: number; agent: string; body: string; meta: string };

// Cap retained events so a long-lived tab can't grow these arrays without bound.
const MAX_LOG_ROWS = 500;

function formatCost(cost: number): string {
  if (cost === 0) return '$0';
  if (cost < 0.001) return `$${(cost * 1000).toFixed(2)}m`;
  if (cost < 1) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}

function llmRow(e: LlmEvent): Row {
  const totalIn = e.input_tokens + e.cache_read_tokens + e.cache_creation_tokens;
  const body = `${e.task_label} · ${e.model} · ${totalIn.toLocaleString()} in / ${e.output_tokens.toLocaleString()} out`;
  return {
    kind: 'llm',
    ts: e.ts,
    agent: 'Matilda',
    body,
    meta: formatCost(e.cost_usd),
  };
}

function pipelineRow(e: RunProgressEvent): Row {
  return { kind: 'pipeline', ts: e.ts, agent: e.agent, body: e.body, meta: null };
}

export const ActivityLogButton: FunctionComponent = () => {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pipelineEvents, setPipelineEvents] = useState<RunProgressEvent[]>(() => getRunProgressBuffer());
  const [llmEvents, setLlmEvents] = useState<LlmEvent[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const offEvent = subscribeRunProgress(e => setPipelineEvents(cur => [...cur, e].slice(-MAX_LOG_ROWS)));
    const offClear = subscribeRunProgressClear(() => setPipelineEvents([]));
    return () => {
      offEvent();
      offClear();
    };
  }, []);

  // Hold the LLM-log SSE open only while the panel is open. A connection on
  // every page would keep the document from ever reaching network-idle; the
  // route replays a snapshot on connect, so reopening loses no recent history.
  useEffect(() => {
    if (!open) return;
    const handle = streamApi(llmLogStreamRoute, {});
    (async () => {
      for await (const frame of handle.data) {
        const ev = frame.data;
        if (ev.kind === 'snapshot') setLlmEvents(ev.entries as LlmEvent[]);
        else if (ev.kind === 'event')
          setLlmEvents(cur => [...cur, ev.entry as LlmEvent].slice(-MAX_LOG_ROWS));
      }
    })();
    return () => handle.stop();
  }, [open]);

  const rows = useMemo<Row[]>(() => {
    const merged: Row[] = [...pipelineEvents.map(pipelineRow), ...llmEvents.map(llmRow)];
    merged.sort((a, b) => a.ts - b.ts);
    return merged;
  }, [pipelineEvents, llmEvents]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight });
    }
  }, [open, rows.length]);

  function clearAll(): void {
    clearRunProgress();
    setLlmEvents([]);
  }

  const total = rows.length;
  const llmCount = llmEvents.length;

  const modal =
    open && mounted
      ? createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setOpen(false)}
          >
            <div
              className="bg-white rounded-[var(--radius)] shadow-lg w-[90vw] max-w-4xl max-h-[80vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-[16px] border-b border-line">
                <div>
                  <div className="font-semibold text-ink">Pipeline activity log</div>
                  <div className="text-sm text-ink-2 mt-[4px]">
                    {total === 0
                      ? 'No agent activity yet — click "Run pipeline" to start.'
                      : `${total} entr${total === 1 ? 'y' : 'ies'} · ${llmCount} LLM call${llmCount === 1 ? '' : 's'}.`}
                  </div>
                </div>
                <div className="flex gap-[8px]">
                  <Button size="small" onClick={clearAll} disabled={total === 0}>
                    Clear
                  </Button>
                  <button
                    className="flex items-center justify-center w-[32px] h-[32px] hover:bg-bg-hover rounded-[var(--radius)]"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-[16px]" ref={scrollRef}>
                {total === 0 ? (
                  <div className="text-center text-ink-3 py-[16px]">
                    When the pipeline runs or any agent calls an LLM, every step shows up here in order.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="border-b border-line">
                      <tr>
                        <th className="text-left font-medium text-ink px-[8px] py-[8px] w-[80px]">Time</th>
                        <th className="text-left font-medium text-ink px-[8px] py-[8px] w-[100px]">
                          Agent
                        </th>
                        <th className="text-left font-medium text-ink px-[8px] py-[8px]">Action</th>
                        <th className="text-right font-medium text-ink px-[8px] py-[8px] w-[80px]">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr
                          key={i}
                          className={`border-b border-line last:border-0 ${r.kind === 'llm' ? 'bg-amber-soft bg-opacity-30' : ''}`}
                        >
                          <td className="text-ink-3 px-[8px] py-[8px] font-mono text-xs">
                            <LocalDate value={r.ts} format="HH:mm:ss" />
                          </td>
                          <td className="text-ink-2 px-[8px] py-[8px]">{r.agent}</td>
                          <td className="text-ink px-[8px] py-[8px]">{r.body}</td>
                          <td className="text-right text-ink-3 px-[8px] py-[8px] font-mono text-xs">
                            {r.meta ?? ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <Button onClick={() => setOpen(true)}>Activity log{total > 0 ? ` (${total})` : ''}</Button>
      {modal}
    </>
  );
};
